import type { Address } from "viem"
import type { Config } from "wagmi"
import { action, computed, makeObservable, observable, override, runInAction } from "mobx"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import type { EventEmitter } from "@/domains/library"
import { getAmountsForLiquidity } from "@/domains/manual-cl-core"
import { resolveCoherentSqrtPriceX96 } from "@/domains/thick-v2/utils/resolve-coherent-sqrt-price"

import { THICK_V2_BASE_ADDRESSES } from "../constants"
import { decreaseThickNfpInteraction } from "../interactions"
import type { ManualClPositionRepository } from "../positions/manual-cl-position-repository"
import type { ManualClPositionStore } from "../positions/manual-cl-position-store"
import { humanizeTransactionError } from "../utils/humanize-transaction-error"
import { ManagePositionStoreBase } from "./manage-position-store-base"

/**
 * Manage-position store for "decrease liquidity" (partial withdrawal).
 *
 * The user chooses what percentage of their existing liquidity to withdraw via
 * `setPercentToWithdraw`. The store then computes:
 *
 *   - `liquidityToWithdraw`  — the bigint liquidity delta handed to the NFPM
 *   - `amount0Min` / `amount1Min` — min output amounts with slippage applied
 *
 * On success it publishes `ManualClLiquidityDecreasedEvent` so the position
 * orchestrator's two queries re-execute and the UI reflects the new liquidity
 * balance without a full page refresh.
 */
export class DecreasePositionStore extends ManagePositionStoreBase {
  /**
   * Percentage of the position's liquidity to withdraw. Valid range is
   * [0, 100]. Stored as a number (not a string) to keep slider/input widgets
   * trivial — parsing happens at the view boundary.
   */
  percentToWithdraw: number = 0

  isSubmitting: boolean = false
  submissionError: string | undefined = undefined

  constructor(
    positionRepository: ManualClPositionRepository,
    private readonly chainId: number,
    private readonly config: Config,
    private readonly eventBus: IEventBus,
    private readonly emitter: EventEmitter<EmitEvent>,
    private readonly walletStore: IWalletConnectionStore,
  ) {
    super(positionRepository)
    makeObservable(this, {
      percentToWithdraw: observable,
      isSubmitting: observable,
      submissionError: observable,

      setPercentToWithdraw: action,
      submit: action,
      reset: override,

      liquidityToWithdraw: computed,
      expectedAmount0: computed,
      expectedAmount1: computed,
      amount0Min: computed,
      amount1Min: computed,
      canSubmit: computed,
    })
  }

  private get activePosition(): ManualClPositionStore | undefined {
    return this.position
  }

  setPercentToWithdraw(percent: number): void {
    const clamped = Math.max(0, Math.min(100, percent))
    this.percentToWithdraw = clamped
  }

  get liquidityToWithdraw(): bigint {
    const position = this.activePosition
    if (!position) return 0n
    if (this.percentToWithdraw <= 0) return 0n
    if (this.percentToWithdraw >= 100) return position.liquidity
    const scaledPercent = BigInt(Math.floor(this.percentToWithdraw * 100))
    return (position.liquidity * scaledPercent) / 10_000n
  }

  private get proportionalWithdrawBps(): bigint {
    if (this.percentToWithdraw <= 0) return 0n
    if (this.percentToWithdraw >= 100) return 10_000n
    return BigInt(Math.floor(this.percentToWithdraw * 100))
  }

  private scaleUnderlyingAmount(underlying: bigint): bigint {
    const position = this.activePosition
    if (!position || position.liquidity <= 0n) return 0n
    if (this.liquidityToWithdraw <= 0n) return 0n
    // Withdraw projections should mirror what the user controls: x% of current
    // underlying for this NFT. This avoids a stale/invalid sqrtPrice producing
    // deceptive zeros in the UI while tx execution still succeeds.
    return (underlying * this.proportionalWithdrawBps) / 10_000n
  }

  /**
   * Best-effort sqrt price used for projected output amounts. Prefers pool
   * slot0; falls back to tick-derived so math still runs before slot0 refresh.
   */
  private get resolvedSqrtPriceX96(): bigint | undefined {
    const pool = this.activePosition?.pool
    if (!pool) return undefined
    return resolveCoherentSqrtPriceX96({
      currentSqrtPriceX96: pool.currentSqrtPriceX96,
      currentTick: pool.currentTick,
    })
  }

  /** Projected token0 received pre-slippage (x% of current underlying). */
  get expectedAmount0(): bigint {
    const position = this.activePosition
    if (!position) return 0n
    const proportional = this.scaleUnderlyingAmount(position.underlyingAmount0)
    const sqrtPriceX96 = this.resolvedSqrtPriceX96
    if (sqrtPriceX96 === undefined) return proportional
    try {
      const { amount0 } = getAmountsForLiquidity({
        sqrtPriceX96,
        tickLower: position.tickLower,
        tickUpper: position.tickUpper,
        liquidity: this.liquidityToWithdraw,
      })
      if (amount0 === 0n && proportional > 0n) return proportional
      return amount0
    } catch {
      return proportional
    }
  }

  /** Projected token1 received pre-slippage (x% of current underlying). */
  get expectedAmount1(): bigint {
    const position = this.activePosition
    if (!position) return 0n
    const proportional = this.scaleUnderlyingAmount(position.underlyingAmount1)
    const sqrtPriceX96 = this.resolvedSqrtPriceX96
    if (sqrtPriceX96 === undefined) return proportional
    try {
      const { amount1 } = getAmountsForLiquidity({
        sqrtPriceX96,
        tickLower: position.tickLower,
        tickUpper: position.tickUpper,
        liquidity: this.liquidityToWithdraw,
      })
      if (amount1 === 0n && proportional > 0n) return proportional
      return amount1
    } catch {
      return proportional
    }
  }

  /**
   * Minimum amount0 the NFPM must return for the tx to succeed. Applies the
   * user-selected slippage to the projected amount from liquidity math.
   */
  get amount0Min(): bigint {
    if (this.expectedAmount0 === 0n) return 0n
    const slippageMultiplier = BigInt(Math.floor((100 - this.slippage) * 100))
    return (this.expectedAmount0 * slippageMultiplier) / 10_000n
  }

  get amount1Min(): bigint {
    if (this.expectedAmount1 === 0n) return 0n
    const slippageMultiplier = BigInt(Math.floor((100 - this.slippage) * 100))
    return (this.expectedAmount1 * slippageMultiplier) / 10_000n
  }

  get canSubmit(): boolean {
    if (!this.activePosition) return false
    if (this.isSubmitting) return false
    if (!this.walletStore.isConnected) return false
    if (this.activeNfpmTokenId === undefined) return false
    return this.liquidityToWithdraw > 0n
  }

  async submit(): Promise<void> {
    const position = this.activePosition
    if (!position) return
    const nfpmTokenId = this.activeNfpmTokenId
    if (nfpmTokenId === undefined) {
      runInAction(() => {
        this.submissionError = "NFP ID is still resolving. Please try again in a moment."
      })
      return
    }
    const account = this.walletStore.connectedAddress?.raw
    if (!account) return

    runInAction(() => {
      this.isSubmitting = true
      this.submissionError = undefined
    })

    const liquidityToWithdraw = this.liquidityToWithdraw
    const liquidityRemaining = position.liquidity - liquidityToWithdraw

    try {
      const deadline = this.getSubmitDeadline()
      const result = await decreaseThickNfpInteraction({
        account,
        chainId: this.chainId,
        config: this.config,
        tokenId: nfpmTokenId,
        poolAddress: position.pool.address.raw as Address,
        liquidityToWithdraw,
        liquidityRemaining,
        pairDisplayName: `${position.pool.token0.symbol}/${position.pool.token1.symbol}`,
        amount0Min: this.amount0Min,
        amount1Min: this.amount1Min,
        slippagePercent: this.slippage,
        deadline,
        nfpManagerAddress: THICK_V2_BASE_ADDRESSES.nfPositionManager,
        emitter: this.emitter,
        eventBus: this.eventBus,
      })

      if (result.success === false) {
        const humanized = humanizeTransactionError(result.error)
        if (humanized !== null) {
          runInAction(() => {
            this.submissionError = humanized
          })
        }
      }
    } catch (error) {
      const humanized = humanizeTransactionError(error)
      if (humanized !== null) {
        runInAction(() => {
          this.submissionError = humanized
        })
      }
    } finally {
      runInAction(() => {
        this.isSubmitting = false
      })
    }
  }

  reset(): void {
    super.reset()
    this.percentToWithdraw = 0
    this.isSubmitting = false
    this.submissionError = undefined
  }
}
